import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    ChevronLeft, Loader2, Lock, Users, X,
    AlertCircle, ChevronRight,
} from "lucide-react";
import axios from "axios";
import { getMemberInitials, getMemberAvatarColor, formatCurrency, getGroupTypeConfig } from "../utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";
const GRADIENT = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso.slice(0, 10); }
};

const fmtAmount = (amount, currency = "GBP") => {
    try { return formatCurrency(amount, currency); }
    catch { return `£${Number(amount || 0).toFixed(2)}`; }
};

// ── Members popup ─────────────────────────────────────────────────────────────
const MembersPopup = ({ members, onClose }) => (
    <div
        className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
        style={{ backgroundColor: "rgba(15,23,42,0.5)" }}
        onClick={onClose}
    >
        <div
            className="w-full md:max-w-sm bg-white"
            style={{ borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, maxHeight: "70vh", overflow: "hidden", display: "flex", flexDirection: "column" }}
            onClick={e => e.stopPropagation()}
        >
            <div className="flex items-center justify-between mb-4">
                <h3 style={{ color: "#111827", fontSize: 17, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                    Members
                </h3>
                <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full" style={{ backgroundColor: "#F3F4F6" }}>
                    <X className="w-4 h-4" style={{ color: "#6B7280" }} />
                </button>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
                {members.map((m, i) => {
                    const name = m.display_name || "Unknown";
                    const initials = getMemberInitials(name);
                    const bg = getMemberAvatarColor(name);
                    return (
                        <div key={i} className="flex items-center gap-3 py-3" style={{ borderBottom: i < members.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                            <div className="flex items-center justify-center flex-shrink-0"
                                style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: bg, color: "#FFFFFF", fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                                {initials}
                            </div>
                            <div className="min-w-0">
                                <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{name}</p>
                                <p className="truncate" style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>{m.email || ""}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    </div>
);

// ── Receipt row ───────────────────────────────────────────────────────────────
const ReceiptRow = ({ receipt, onClick }) => {
    if (receipt.deleted) {
        return (
            <div className="flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F3F4F6", opacity: 0.55 }}>
                <div className="flex items-center justify-center flex-shrink-0"
                    style={{ width: 44, height: 44, borderRadius: 14, backgroundColor: "#F3F4F6" }}>
                    <AlertCircle className="w-5 h-5" style={{ color: "#9CA3AF" }} />
                </div>
                <div className="flex-1 min-w-0">
                    <p style={{ color: "#9CA3AF", fontSize: 15, fontStyle: "italic", fontFamily: "DM Sans, sans-serif" }}>
                        Receipt no longer available
                    </p>
                    <p style={{ color: "#D1D5DB", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                        by {receipt.shared_by} · {fmtDate(receipt.shared_at)}
                    </p>
                </div>
            </div>
        );
    }

    const initials = (receipt.merchant || "?").slice(0, 2).toUpperCase();
    const bg = getMemberAvatarColor(receipt.merchant || "");

    return (
        <button
            onClick={() => onClick(receipt)}
            className="w-full flex items-center gap-3 py-3 text-left"
            style={{ borderBottom: "1px solid #F3F4F6" }}
        >
            <div className="flex items-center justify-center flex-shrink-0"
                style={{ width: 44, height: 44, borderRadius: 14, backgroundColor: bg, color: "#FFFFFF", fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                {initials}
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-semibold truncate" style={{ color: "#111827", fontSize: 15, fontFamily: "Manrope, sans-serif" }}>
                    {receipt.merchant}
                </p>
                <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                    by {receipt.shared_by} · {fmtDate(receipt.transaction_date)}
                </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                <span style={{ color: "#111827", fontSize: 15, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                    {fmtAmount(receipt.amount, receipt.currency)}
                </span>
                <ChevronRight className="w-4 h-4" style={{ color: "#D1D5DB" }} />
            </div>
        </button>
    );
};

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CircleSummaryPage() {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { user: authUser } = useAuth();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [showMembers, setShowMembers] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await axios.get(`${API}/groups/${groupId}/summary`, { withCredentials: true });
                if (res.data.success) setData(res.data);
                else setError("Could not load summary.");
            } catch (e) {
                setError("Could not load summary. You may not have access.");
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [groupId]);

    const handleReceiptClick = (receipt) => {
        if (receipt.item_id) navigate(`/receipts/${receipt.item_id}`);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F9FAFB" }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: PRIMARY }} />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6" style={{ backgroundColor: "#F9FAFB" }}>
                <AlertCircle className="w-10 h-10" style={{ color: "#EF4444" }} />
                <p style={{ color: "#374151", fontFamily: "DM Sans, sans-serif", textAlign: "center" }}>{error}</p>
                <button onClick={() => navigate(-1)} style={{ color: PRIMARY, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>Go back</button>
            </div>
        );
    }

    const { group, members, summary, receipts } = data;
    const cfg = getGroupTypeConfig(group.group_type);

    // ── Shared content ────────────────────────────────────────────────────────
    const content = (
        <div style={{ paddingBottom: 40 }}>
            {/* Stats cards */}
            <div className="flex gap-3 mb-6">
                {[
                    { label: "Total shared", value: fmtAmount(summary.grand_total) },
                    { label: "Receipts", value: summary.receipt_count },
                    { label: "Members", value: summary.member_count },
                ].map((s, i) => (
                    <div key={i} className="flex-1 text-center" style={{ backgroundColor: "#FFFFFF", borderRadius: 16, padding: "14px 8px", boxShadow: "0 2px 10px rgba(15,23,42,0.06)" }}>
                        <p style={{ color: "#111827", fontSize: i === 0 ? 17 : 22, fontWeight: 800, fontFamily: "Manrope, sans-serif", letterSpacing: -0.3 }}>{s.value}</p>
                        <p style={{ color: "#9CA3AF", fontSize: 11, fontFamily: "DM Sans, sans-serif", marginTop: 3 }}>{s.label}</p>
                    </div>
                ))}
            </div>

            {/* Members button */}
            <button
                onClick={() => setShowMembers(true)}
                className="w-full flex items-center gap-3 mb-4"
                style={{ backgroundColor: "#FFFFFF", borderRadius: 16, padding: "14px 16px", boxShadow: "0 2px 10px rgba(15,23,42,0.06)", textAlign: "left" }}
            >
                <div className="flex -space-x-2">
                    {members.slice(0, 4).map((m, i) => {
                        const name = m.display_name || "?";
                        return (
                            <div key={i} className="flex items-center justify-center flex-shrink-0"
                                style={{ width: 32, height: 32, borderRadius: 999, backgroundColor: getMemberAvatarColor(name), border: "2px solid #FFFFFF", color: "#FFFFFF", fontSize: 11, fontWeight: 700, fontFamily: "Manrope, sans-serif", zIndex: members.length - i }}>
                                {getMemberInitials(name)}
                            </div>
                        );
                    })}
                </div>
                <div className="flex-1 min-w-0">
                    <p style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>
                        {summary.member_count} member{summary.member_count !== 1 ? "s" : ""}
                    </p>
                    <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>Tap to see names &amp; emails</p>
                </div>
                <Users className="w-4 h-4 flex-shrink-0" style={{ color: "#D1D5DB" }} />
            </button>

            {/* Per-member breakdown */}
            <div style={{ backgroundColor: "#FFFFFF", borderRadius: 16, padding: "16px 16px 8px", boxShadow: "0 2px 10px rgba(15,23,42,0.06)", marginBottom: 16 }}>
                <h2 style={{ color: "#111827", fontSize: 15, fontWeight: 700, fontFamily: "Manrope, sans-serif", marginBottom: 12 }}>
                    Spending breakdown
                </h2>
                {(summary.per_member || []).map((row, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5"
                        style={{ borderBottom: i < summary.per_member.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center flex-shrink-0"
                                style={{ width: 28, height: 28, borderRadius: 999, backgroundColor: getMemberAvatarColor(row.name), color: "#FFFFFF", fontSize: 10, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                                {getMemberInitials(row.name)}
                            </div>
                            <p style={{ color: "#374151", fontSize: 14, fontFamily: "DM Sans, sans-serif" }}>{row.name.split(" ")[0]}</p>
                        </div>
                        <p style={{ color: "#111827", fontSize: 14, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                            {fmtAmount(row.total)}
                        </p>
                    </div>
                ))}
            </div>

            {/* Receipt list */}
            <div style={{ backgroundColor: "#FFFFFF", borderRadius: 16, padding: "16px 16px 8px", boxShadow: "0 2px 10px rgba(15,23,42,0.06)" }}>
                <h2 style={{ color: "#111827", fontSize: 15, fontWeight: 700, fontFamily: "Manrope, sans-serif", marginBottom: 4 }}>
                    Shared receipts
                </h2>
                {receipts.length === 0 ? (
                    <p style={{ color: "#9CA3AF", fontSize: 14, fontFamily: "DM Sans, sans-serif", paddingTop: 12, paddingBottom: 8 }}>
                        No receipts were shared in this circle.
                    </p>
                ) : (
                    receipts.map((r, i) => (
                        <ReceiptRow key={r.share_id || i} receipt={r} onClick={handleReceiptClick} />
                    ))
                )}
            </div>
        </div>
    );

    // ── Mobile layout ─────────────────────────────────────────────────────────
    if (isMobile) {
        return (
            <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#F9FAFB" }}>
                {/* Blue header */}
                <header style={{ background: GRADIENT, padding: "16px 20px 28px", borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
                    <div className="flex items-center gap-3 mb-5">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center justify-center"
                            style={{ width: 36, height: 36, borderRadius: 999, backgroundColor: "rgba(255,255,255,0.18)", color: "#FFFFFF" }}
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                        <Lock className="w-4 h-4" style={{ color: "rgba(255,255,255,0.7)" }} />
                        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>Circle closed</p>
                    </div>
                    <h1 style={{ color: "#FFFFFF", fontSize: 26, fontWeight: 800, fontFamily: "Manrope, sans-serif", letterSpacing: -0.5, lineHeight: 1.2 }}>
                        {group.name}
                    </h1>
                    <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 4 }}>
                        {cfg.label} · Closed {fmtDate(group.closed_at)}
                        {group.closed_by_name ? ` by ${group.closed_by_name.split(" ")[0]}` : ""}
                    </p>
                </header>

                <main className="flex-1 px-4 pt-5">
                    {content}
                </main>

                {showMembers && <MembersPopup members={members} onClose={() => setShowMembers(false)} />}
            </div>
        );
    }

    // ── Web layout ────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen" style={{ backgroundColor: "#F9FAFB" }}>
            {/* Blue banner */}
            <div style={{ background: GRADIENT, padding: "28px 40px 32px" }}>
                <div style={{ maxWidth: 700, margin: "0 auto" }}>
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 mb-4"
                        style={{ color: "rgba(255,255,255,0.8)", fontSize: 14, fontFamily: "DM Sans, sans-serif" }}
                    >
                        <ChevronLeft className="w-4 h-4" /> Back
                    </button>
                    <div className="flex items-center gap-2 mb-1">
                        <Lock className="w-4 h-4" style={{ color: "rgba(255,255,255,0.7)" }} />
                        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>Circle closed</p>
                    </div>
                    <h1 style={{ color: "#FFFFFF", fontSize: 30, fontWeight: 800, fontFamily: "Manrope, sans-serif", letterSpacing: -0.5 }}>
                        {group.name}
                    </h1>
                    <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, fontFamily: "DM Sans, sans-serif", marginTop: 4 }}>
                        {cfg.label} · Closed {fmtDate(group.closed_at)}
                        {group.closed_by_name ? ` by ${group.closed_by_name.split(" ")[0]}` : ""}
                    </p>
                </div>
            </div>

            <main style={{ maxWidth: 700, margin: "0 auto", padding: "28px 24px" }}>
                {content}
            </main>

            {showMembers && <MembersPopup members={members} onClose={() => setShowMembers(false)} />}
        </div>
    );
}